/**
 * Ecom signup form controller.
 *
 * Owns: form submission, field validation, country/top-country flow,
 * recaptcha, success redirect. Delegates DOM interactions (apps-config
 * rows, stations stepper, modal, pricing summaries) to PriceCalculator.
 *
 * Reads required field config from the global `formFields` const emitted
 * by the Twig template (page-specific, must remain in the template).
 */

import PriceCalculator from './priceCalculator';

const SUBMIT_LABELS = {
  en: 'Submitting...',
};

const ALERT_OPT_IN =
  'Please confirm that you do not have an active Broadcaster license.';
const ALERT_GENERIC = 'Error. Try again later';
const ALERT_NO_STATIONS =
  'Each app must have at least 1 station (broadcast or IP)';
const SUBMITTING_TEXT = 'Submitting...';

const t = (el, key) => el.dataset[`i18n${key}`] || '';

class SignUp {
  constructor() {
    this.form = document.getElementById('signup-form');
    if (!this.form) return;

    this.priceCalculator = new PriceCalculator(this.form);
    this.submitBtn = document.getElementById('signup-form--submit');
    this.originalText = this.submitBtn?.textContent || '';
    this.formData = new FormData();
    this.requiredFields = typeof formFields !== 'undefined' ? formFields : [];
    this.countrySelect = this.form.querySelector('select[data-top-countries]');
    this.optInCheckbox = document.getElementById('license-opt-in-checkbox');
    this.optInContainer = document.getElementById('license-opt-in');
    this.isProcessing = false;
    this.recaptchaWidget = null;
    this.recaptchaResponse = null;

    this.bindEvents();
    this.handleUrlPrefill();
    this.initRecaptcha();
  }

  handleUrlPrefill() {
    const orderId = new URLSearchParams(window.location.search).get('orderId');
    if (!orderId) return;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'orderId';
    input.value = orderId;
    this.form.appendChild(input);
  }

  bindEvents() {
    if (this.countrySelect) {
      this.countrySelect.addEventListener('change', () => this.toggleOptIn());
    }
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    this.requiredFields.forEach((fieldConfig) => {
      if (fieldConfig.type === 'text') {
        const field = document.getElementById(fieldConfig.id);
        field?.addEventListener('input', () => {
          if (field.value.trim()) field.classList.remove('error');
        });
      }
      if (fieldConfig.type === 'checkbox') {
        document.addEventListener('change', (e) => {
          if (!e.target.matches(`input[name="${fieldConfig.id}"]`)) return;
          const checkboxes = document.querySelectorAll(
            `input[name="${fieldConfig.id}"]`
          );
          const anyChecked = Array.from(checkboxes).some((c) => c.checked);
          if (anyChecked)
            checkboxes.forEach((c) => c.classList.remove('error'));
        });
      }
    });
  }

  async executeRecaptcha() {
    if (
      !this.recaptchaResponse ||
      !(this.recaptchaWidget || this.recaptchaWidget === 0) ||
      this.recaptchaResponse.value !== ''
    ) {
      return true;
    }

    try {
      const token = await new Promise((resolve, reject) => {
        grecaptcha
          .execute(this.recaptchaWidget)
          .then((token) => {
            if (token) {
              resolve(token);
            } else {
              reject(new Error('reCAPTCHA token is empty'));
            }
          })
          .catch(reject);
      });
      this.recaptchaResponse.value = token;
      this.formData.set('g-recaptcha-response', token);
      return true;
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      alert('Error sending the form. Please try again.');
      return false;
    }
  }

  async onSubmit(e) {
    e.preventDefault();
    if (this.isProcessing) return;

    if (!this.validateForm()) {
      this.resetSubmitButton();
      return;
    }

    this.formData.set('category', this.form.dataset.category);
    const csrf = this.form.querySelector('[name$="CSRF_TOKEN"]');
    if (csrf) this.formData.set(csrf.name, csrf.value);

    // Handle reCAPTCHA for initial submission
    if (!(await this.executeRecaptcha())) return;

    if (!(await this.handleCountryFlow())) return;

    // If we went through the country flow and token was used, get a new one
    if (!(await this.executeRecaptcha())) return;

    this.isProcessing = true;
    this.setSubmitting();

    try {
      const response = await this.postAction(
        '/actions/ecommerce/order/save',
        this.formData
      );
      if (!response.ok) throw new Error(response.statusText);
      const data = await response.json();
      if (data.success) {
        window.location.href = data.redirectUrl || data.stripeUrl || '/success';
      } else {
        console.error(data);
        alert(data.error || data.message || ALERT_GENERIC);
      }
    } catch (error) {
      console.error(error);
      alert(ALERT_GENERIC);
    } finally {
      this.isProcessing = false;
      this.resetSubmitButton();
    }
  }

  setSubmitting() {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = true;
    this.submitBtn.textContent = SUBMITTING_TEXT;
  }

  resetSubmitButton() {
    if (!this.submitBtn) return;
    this.submitBtn.disabled = false;
    this.submitBtn.textContent = this.originalText;
  }

  // POSTs an action, retrying once with a refreshed CSRF token when the server
  // rejects the submission as invalid (HTTP 400/403). Craft's cookie-based CSRF
  // token can go stale if the page outlives a session/user change or a deploy.
  async postAction(url, data) {
    let response = await fetch(url, { method: 'POST', body: data });
    if (response.status === 400 || response.status === 403) {
      if (await this.refreshCsrfToken(data)) {
        response = await fetch(url, { method: 'POST', body: data });
      }
    }
    return response;
  }

  // Re-reads a freshly rendered CSRF token from the current page.
  async refreshCsrfToken(data) {
    try {
      const res = await fetch(window.location.pathname + window.location.search);
      if (!res.ok) return false;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('[name$="CSRF_TOKEN"]');
      if (!fresh) return false;
      const current = this.form?.querySelector('[name$="CSRF_TOKEN"]');
      if (current) current.value = fresh.value;
      this.formData?.set(fresh.name, fresh.value);
      if (data && data !== this.formData) data.set(fresh.name, fresh.value);
      return true;
    } catch (e) {
      return false;
    }
  }

  // ── Validation ────────────────────────────────────────────────────

  validateForm() {
    this.formData = new FormData(this.form);
    let firstError = null;

    for (const fieldConfig of this.requiredFields) {
      const check = this.checkField(fieldConfig);
      if (check.error && !firstError) {
        firstError = check;
      }
    }

    const apps = this.priceCalculator?.getApps() || [];
    if (apps.length > 0) {
      const appsPayload = [];
      for (const app of apps) {
        if (app.broadcastStations + app.ipStations < 1) {
          if (!firstError)
            firstError = {
              field: document.querySelector('.js-broadcast-stations'),
              message: ALERT_NO_STATIONS,
            };
        } else {
          appsPayload.push(app);
        }
      }
      if (appsPayload.length > 0) {
        this.formData.set('apps', JSON.stringify(appsPayload));
      }
    }

    if (!firstError) return true;
    firstError.field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    firstError.field?.focus();
    alert(firstError.message);
    return false;
  }

  checkField(fieldConfig) {
    if (fieldConfig.type === 'text' || fieldConfig.type === 'textarea') {
      const field = document.getElementById(fieldConfig.id);
      if (!field) return {};
      if (fieldConfig.required && !field.classList.contains('hidden')) {
        if (!field.value.trim()) {
          field.classList.add('error');
          return { error: true, field, message: fieldConfig.error };
        }
        field.classList.remove('error');
      } else if (field) {
        this.formData.set(fieldConfig.id, field.value.trim());
      }
    } else if (fieldConfig.type === 'checkbox') {
      const checkboxes = document.querySelectorAll(
        `input[name="${fieldConfig.id}"]`
      );
      const checked = Array.from(checkboxes).filter((c) => c.checked);
      if (checked.length > 0) {
        checkboxes.forEach((c) => c.classList.remove('error'));
        this.formData.set(
          fieldConfig.id,
          checked.map((c) => c.value).join(', ')
        );
      } else if (fieldConfig.required) {
        if (checkboxes.length > 0) {
          checkboxes.forEach((c) => c.classList.add('error'));
          return {
            error: true,
            field: checkboxes[0],
            message: fieldConfig.error,
          };
        }
      } else {
        checkboxes.forEach((c) => c.classList.remove('error'));
      }
    } else if (fieldConfig.type === 'select') {
      const field = document.getElementById(fieldConfig.id);
      if (field) this.formData.set(fieldConfig.id, field.value);
    } else if (fieldConfig.type === 'hidden') {
      const field = document.getElementById(fieldConfig.id);
      if (field) this.formData.set(fieldConfig.id, field.value);
    }
    return {};
  }

  // ── Opt-in / country flow ─────────────────────────────────────────

  toggleOptIn() {
    if (!this.countrySelect || !this.optInContainer || !this.optInCheckbox)
      return;
    const topCountries = this.parseTopCountries();
    if (!topCountries.length) {
      this.optInContainer.classList.add('hidden');
      this.optInCheckbox.required = false;
      this.optInCheckbox.checked = false;
      return;
    }
    const selected = this.countrySelect.value;
    if (selected && !topCountries.includes(selected)) {
      this.optInContainer.classList.remove('hidden');
      this.optInCheckbox.required = true;
    } else {
      this.optInContainer.classList.add('hidden');
      this.optInCheckbox.required = false;
      this.optInCheckbox.checked = false;
    }
  }

  parseTopCountries() {
    try {
      return JSON.parse(this.countrySelect?.dataset.topCountries || '[]');
    } catch (e) {
      return [];
    }
  }

  async handleCountryFlow() {
    if (!this.countrySelect?.value) return true;
    const topCountries = this.parseTopCountries();
    if (!topCountries.length) return true;
    if (!topCountries.includes(this.countrySelect.value)) {
      return this.checkOptIn();
    }
    return this.handleTopCountry();
  }

  checkOptIn() {
    if (
      !this.optInContainer ||
      this.optInContainer.classList.contains('hidden')
    ) {
      return true;
    }
    if (!this.optInCheckbox?.checked) {
      alert(ALERT_OPT_IN);
      this.optInCheckbox?.focus();
      return false;
    }
    return true;
  }

  async handleTopCountry() {
    const modalTitle = this.form.dataset.countryModalTitle || '';
    const modalText = this.form.dataset.countryModalText || '';
    const entryId = await this.saveStartedOrder();

    // Clear the used reCAPTCHA token after background save
    if (this.recaptchaResponse) {
      this.recaptchaResponse.value = '';
    }
    this.formData.delete('g-recaptcha-response');

    // The modal resolves with the chosen action: 'stripe' (user has a license) or
    // 'unlicensed' (user clicked one of the extraButtons = has no license).
    const choice = await this.showCountryModal(modalTitle, modalText);
    if (choice === 'unlicensed' && entryId) {
      await this.finalizeUnlicensed(entryId);
      return false;
    }
    if (entryId) {
      this.formData.set('orderId', String(entryId));
    }
    return true;
  }

  // Background save before the license modal. Creates the order as "started" - no
  // email is sent here. Only actionUnlicensed (triggered by an extraButton click)
  // marks the order as unlicensed and sends the unlicensed email.
  async saveStartedOrder() {
    const payload = new FormData();
    this.formData.forEach((value, key) => payload.append(key, value));
    payload.set('started', '1');
    try {
      const response = await this.postAction(
        '/actions/ecommerce/order/save',
        payload
      );
      this.recaptchaResponse.value = '';
      if (!response.ok) return null;
      const data = await response.json();
      return data.success && data.entryId ? data.entryId : null;
    } catch (e) {
      console.error('Background save failed', e);
      return null;
    }
  }

  // Finalize an order as unlicensed (sets status + sends the unlicensed email) and
  // redirect to the returned license page.
  async finalizeUnlicensed(orderId) {
    const payload = new FormData();
    payload.set('orderId', String(orderId));
    payload.set('category', this.form.dataset.category || '');
    const csrf = this.form.querySelector('[name$="CSRF_TOKEN"]');
    if (csrf) payload.set(csrf.name, csrf.value);
    try {
      const response = await this.postAction(
        '/actions/ecommerce/order/unlicensed',
        payload
      );
      if (!response.ok) return false;
      const data = await response.json();
      if (data.success && data.redirectUrl) {
        window.location.href = data.redirectUrl;
      }
      return data.success === true;
    } catch (e) {
      console.error('Unlicensed finalize failed', e);
      return false;
    }
  }

  showCountryModal(title, text) {
    return new Promise((resolve) => {
      const modal = document.getElementById('country-modal');
      const titleEl = document.getElementById('country-modal-title');
      const textEl = document.getElementById('country-modal-text');
      const yesBtn = document.getElementById('country-modal-yes');
      const extraButtons = modal
        ? Array.from(modal.querySelectorAll('.country-modal-extra'))
        : [];
      if (!modal || !titleEl || !textEl || !yesBtn) {
        resolve('stripe');
        return;
      }
      titleEl.textContent = title;
      textEl.textContent = text;
      modal.classList.remove('hidden');
      modal.classList.add('flex');

      const close = () => {
        modal.classList.add('hidden');
        modal.classList.remove('flex');
        yesBtn.removeEventListener('click', onYes);
        extraButtons.forEach((btn) => btn.removeEventListener('click', onExtra));
      };
      const onYes = () => {
        close();
        resolve('stripe');
      };
      const onExtra = (e) => {
        e.preventDefault();
        close();
        resolve('unlicensed');
      };
      yesBtn.addEventListener('click', onYes);
      extraButtons.forEach((btn) => btn.addEventListener('click', onExtra));
    });
  }

  // ── recaptcha ─────────────────────────────────────────────────────

  initRecaptcha() {
    const container = this.form.querySelector('[recaptcha-container]');
    if (!container) return;

    this.recaptchaResponse = this.form.querySelector(
      '[name="g-recaptcha-response"]'
    );
  }

  renderRecaptchaWidget() {
    const container = this.form.querySelector('[recaptcha-container]');
    if (!container || !window.RECAPTCHA_SITE_KEY || typeof grecaptcha === 'undefined') return;

    this.recaptchaWidget = grecaptcha.render(container, {
      sitekey: window.RECAPTCHA_SITE_KEY,
      size: 'invisible',
    });
  }
}

let signUpInstance = null;

const initSignUp = () => {
  if (document.getElementById('signup-form')) {
    signUpInstance = new SignUp();
    return signUpInstance;
  }
  return null;
};

window.signUp = initSignUp;

// Global callback for reCAPTCHA script load
window.onRecaptchaloadCallbackEcom = () => {
  if (signUpInstance) {
    signUpInstance.renderRecaptchaWidget();
  }
};

export default initSignUp;
